import { makeCygnusApiCall } from "../../../api/api";
import { Dispatch, SetStateAction } from "react";
import { CustomToaster } from "../../reusable_components/Toasters/toaster";
import { append, GridApiProps } from "../../reusable_components/CygnusGrid/CygnusGrid.utils";
import { SendForApprovalResponse, SendForApprovalResponseData } from "./ClientsGrid.utils";

/**
 * Sends one or more clients for approval.
 * - Skips clients already marked as approved.
 * - Uses bulk submission for selected rows or single clientMasterId.
 *
 * @param selectedRows - Selected client rows from grid.
 * @param setSendForApprovalBulkApiProgress - Updates API loading state.
 * @param setSelectedRows - Clears selected rows after success.
 * @param deselectRows - Function to deselect rows in the grid.
 */
export const sendForApproval = async (
  selectedRows: any[],
  setSendForApprovalBulkApiProgress: Dispatch<SetStateAction<boolean>> = () => {},
  setSelectedRows: Dispatch<any[]> = () => {},
  deselectRows: () => void = () => {}
): Promise<SendForApprovalResponse | null> => {
  setSendForApprovalBulkApiProgress(true);

  if (selectedRows.length === 0) {
    CustomToaster({
      type: "error",
      customized_message: "No valid clients found for approval.",
      api_message: "",
    });
    setSendForApprovalBulkApiProgress(false);
    return null;
  }

  try {
    const response = await makeCygnusApiCall({
      type: "POST",
      payload: selectedRows,
      url: `/forms/send_for_approval/`,
      show_message: false,
      isblob: false,
      forMDM: true,
    });

    setSendForApprovalBulkApiProgress(false);

    // Handle undefined/null response
    if (!response) {
      CustomToaster({
        type: "error",
        customized_message: "No response from server. Please try again.",
        api_message: "",
      });
      return null;
    }

    // Safely extract data with defaults
    const data: SendForApprovalResponseData = response.data ?? {
      processedClients: [],
      invalidClients: [],
      totalRequested: selectedRows.length,
      totalProcessed: 0,
      totalInvalid: 0,
    };

    // Build normalized response
    const normalizedResponse: SendForApprovalResponse = {
      success: response.success ?? false,
      message: response.message ?? "",
      data: data,
    };

    // Show success toast only if clients were actually processed
    if (data.totalProcessed > 0) {
      CustomToaster({
        type: "success",
        customized_message: `${data.totalProcessed} client(s) sent for approval.`,
        api_message: "",
      });
      deselectRows();
      setSelectedRows([]);
    }

    return normalizedResponse;

  } catch (error) {
    console.error("[sendForApproval] Exception:", error);
    setSendForApprovalBulkApiProgress(false);
    CustomToaster({
      type: "error",
      customized_message: "An error occurred while sending for approval.",
      api_message: "",
    });
    return null;
  }
};

/**
 * Creates one or more clients directly in CCH.
 * - Supports bulk creation.
 * - Optionally auto-approves before creation.
 *
 * @param selectedRows - Selected client rows from grid.
 * @param setCreateInCCHApiProgress - Updates API loading state.
 * @param setSelectedRows - Clears selected rows after success.
 * @param deselectRows - Function to deselect rows in the grid.
 */
export const createInCCH = async (
  selectedRows: any[],
  setCreateInCCHApiProgress: Dispatch<
    SetStateAction<boolean>
  > = () => {}
) => {
  setCreateInCCHApiProgress(true);

  const clientMasterIds = Array.isArray(selectedRows)
    ? selectedRows
        .map((r) =>
          r?.ClientMasterId
        )
    : [];

  if (clientMasterIds.length === 0) {
    CustomToaster({
      type: "error",
      customized_message: "No valid clients found for CCH creation.",
      api_message: "",
    });
    setCreateInCCHApiProgress(false);
    return;
  }

  const response = await makeCygnusApiCall({
    type: "POST",
    payload: clientMasterIds,
    url: `/clients/create_in_cch/`,
    show_message: true,
    customized_success_message:
      "Client(s) submitted for CCH creation. The process will run in the background.",
    customized_failure_message:
      "Unable to submit client(s) for CCH creation. Please try again. If the issue persists, contact support.",
    customized_error_message:
      "An unexpected error occurred while submitting client(s) for CCH creation. Please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
  });

  setCreateInCCHApiProgress(false);

  if (response.success) {
    return true;
  }
};


/**
 * Fetches paginated client data for the firm.
 *
 * @param props - Pagination and search text.
 * @returns Object containing records and total count.
 */
export const fetchFirmClients = async ({
  offset,
  limit,
  searchText,
  sourceCode,
  filter,
}: GridApiProps): Promise<{ records: any[]; totalCount: number }> => {
  const page = Math.floor(offset / limit) + 1;

  // Build query string safely
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("page_size", String(limit));
  if (searchText != null && searchText !== "")
    params.set("search_text", String(searchText));
  if (sourceCode) params.set("SourceSystemCode", String(sourceCode));

  // Add ALL filter keys (coming from CygnusGrid's filterKeyMap)
  const append = (key: string, val: any) => {
    if (val == null || val === "") return;
    if (Array.isArray(val)) {
      if (val.length === 0) return;
      params.set(key, JSON.stringify(val.map((v) => String(v).trim())));
    } else if (val instanceof Date) {
      params.set(key, val.toISOString());
    } else {
      params.set(key, String(val));
    }
  };

  Object.entries(filter ?? {}).forEach(([k, v]) => append(k, v));

  const url = `/all_clients/?${params.toString()}`;

  const response = await makeCygnusApiCall({
    type: "GET",
    url,
    show_message: true,
    customized_success_message: "Fetched client data successfully.",
    customized_failure_message:
      "Unable to fetch client data, please try again.",
    customized_error_message:
      "Error occurred while fetching client data. Please contact support.",
    forMDM: true,
  });

  if (response.success) {
    const records = response.data?.records || [];
    const totalRecords: number = response.data?.total_records || 0;
    return { records, totalCount: totalRecords };
  } else {
    return { records: [], totalCount: 0 };
  }
};

export const fetchClientGroups = async (
  groupTypeCode: string = ""
  // setClientSearchAPIProgress: (arg: boolean) => void
) => {
  try {
    const response = await makeCygnusApiCall({
      type: "POST",
      url: "/dropdown/fetch_options/",
      payload: {
        SelectedApiKey: "get_group_cch",
      },
      show_message: false,
      customized_success_message: "Dropdown options fetched successfully.",
      customized_failure_message: "Failed to fetch dropdown options.",
      customized_error_message: "Something went wrong while loading options.",
      isblob: false,
      forMDM: true,
    });

    if (!response?.success || !Array.isArray(response.data)) return;
    return response.data;
  } catch (err) {
    console.error("fetchOptions error:", err);
  } finally {
    // setClientSearchAPIProgress(false);
  }

  return [];
};

export const fetchPurposes = async () => {
  try {
    const response = await makeCygnusApiCall({
      type: "GET",
      url: "/dropdown/line_of_service/",
      payload: {
        SelectedApiKey: "get_staff_cch",
      },
      show_message: false,
      customized_success_message:
        "Line of service options fetched successfully.",
      customized_failure_message: "Failed to fetch line of service options.",
      customized_error_message: "Something went wrong while loading options.",
      isblob: false,
      forMDM: true,
    });

    if (!response?.success || !Array.isArray(response.data)) return;
    return response.data;
  } catch (err) {
    console.error("fetchOptions error:", err);
  } finally {
    // setClientSearchAPIProgress(false);
  }

  return [];
};

export const fetchPrimaryPartners = async () => {
  try {
    const response = await makeCygnusApiCall({
      type: "POST",
      url: "/dropdown/fetch_options/",
      payload: {
        SelectedApiKey: "get_staff_cch",
      },
      show_message: false,
      customized_success_message: "Partner options fetched successfully.",
      customized_failure_message: "Failed to fetch partner options.",
      customized_error_message: "Something went wrong while loading options.",
      isblob: false,
      forMDM: true,
    });

    if (!response?.success || !Array.isArray(response.data)) return;
    return response.data;
  } catch (err) {
    console.error("fetchOptions error:", err);
  } finally {
    // setClientSearchAPIProgress(false);
  }

  return [];
};

/**
 * Fetch client types (reference data with domain = ClientType).
 * @param {string} domain - ReferenceDataDomain name (e.g., "ClientType").
 * @returns {Promise<any>} List of client type values.
 */
export const fetchReferenceData = async (
  domain: string,
): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/reference_data?ReferenceDataDomain=${encodeURIComponent(domain)}`,
    show_message: true,
    customized_success_message: "Fetched Form client type data.",
    customized_failure_message:
      "Unable to fetch your client type data, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occurred while fetching your client type data, please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    return response.data;
  }
};

/**
 * Fetch dropdown options for a given API key.
 * @param {string} apiKey - The API key identifying which options to fetch.
 * @param {boolean} showMessage - The flag for displaying API message.
 * @returns {Promise<any>} List of options returned by the backend.
 */
export const fetchOptions = async (
  apiKey: string,
  showMessage: boolean = false
): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: "POST",
    url: "/dropdown/fetch_options/",
    payload: {
      SelectedApiKey: apiKey,
    },
    show_message: showMessage,
    customized_success_message: "Fetched dropdown options successfully.",
    customized_failure_message:
      "Unable to fetch dropdown options, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occurred while fetching dropdown options, please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    return response.data;
  }

  return [];
};

export type SelectOption = {
  label: string;
  value: string; // the UUID
  id?: string;
  position?: string | null;
  email?: string | null;
};

/**
 * Validates whether selected prospect clients can be deleted.
 * Returns warning / safe counts without actually deleting anything.
 *
 * @param clientMasterIds Array of ClientMasterIds to validate
 * @param deletionReason Combined reason/comment string
 * @returns structured backend response with success / warning info
 */
export const validateDeleteProspectClients = async (
  clientMasterIds: number[],
  deletionReason: string = ""
): Promise<any> => {
  const url = `/all_clients/`;

  const response = await makeCygnusApiCall({
    type: "DELETE",
    url,
    payload: {
      ClientMasterIds: clientMasterIds,
      ValidateOnly: true,
      DeletionReason: deletionReason,
      TargetActive: "N",
    },
    show_message: false,
    forMDM: true,
  });

  const warningIds =
    Array.isArray(response?.warning_client_ids)
      ? response.warning_client_ids
      : typeof response?.warning_client_ids === "string" &&
        response.warning_client_ids.trim() !== ""
      ? response.warning_client_ids.split(",").map((x: string) => x.trim())
      : [];

  return {
    success: response?.success ?? false,
    message: response?.message || "",
    deleted_count: response?.deleted_count ?? 0,
    warning_count: response?.warning_count ?? 0,
    warning_client_ids: warningIds,
  };
};

/**
 * Soft deletes selected prospect clients and linked ELs where allowed.
 *
 * @param clientMasterIds Array of ClientMasterIds to delete
 * @param deletionReason Combined reason/comment string
 * @returns structured backend response with success / warning info
 */
export const deleteProspectClients = async (
  clientMasterIds: number[],
  deletionReason: string = ""
): Promise<any> => {
  const url = `/all_clients/`;

  const response = await makeCygnusApiCall({
    type: "DELETE",
    url,
    payload: {
      ClientMasterIds: clientMasterIds,
      ValidateOnly: false,
      DeletionReason: deletionReason,
      TargetActive: "N",
    },
    show_message: true,
    customized_success_message: "Prospect client(s) deleted successfully.",
    customized_failure_message:
      "Failed to delete prospect client(s). Please try again.",
    customized_error_message:
      "Error occurred while deleting prospect client(s). Please contact support.",
    forMDM: true,
  });

  const warningIds =
    Array.isArray(response?.warning_client_ids)
      ? response.warning_client_ids
      : typeof response?.warning_client_ids === "string" &&
        response.warning_client_ids.trim() !== ""
      ? response.warning_client_ids.split(",").map((x: string) => x.trim())
      : [];

  return {
    success: response?.success ?? false,
    message: response?.message || "",
    deleted_count: response?.deleted_count ?? 0,
    warning_count: response?.warning_count ?? 0,
    warning_client_ids: warningIds,
  };
};
